import { observer } from 'mobx-react-lite'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import {
  availableBookkeepingSoftwareTypes,
  formatAvailableBookkeepingSoftware,
} from '@/features/jobs/bookkeeping/platform-connections/available-bookkeeping-software'
import { allBookkeepingPlatforms } from '@/features/jobs/bookkeeping/platform-connections/bookkeeping-platform'
import {
  formatBookkeepingPlatform,
  formatBookkeepingPlatformLoginName,
  getBookkeepingPlatformLoginUrl,
} from '@/features/jobs/bookkeeping/platform-connections/bookkeeping-platform-connection-formatters'
import {
  BookkeepingGrantAccessDialog,
  type BookkeepingGrantAccessPage,
} from '@/features/jobs/details/components/BookkeepingGrantAccessDialog'
import { match } from '@/utils/pattern-match-util'
import { type BookkeepingGrantAccessDialogViewModel } from './BookkeepingGrantAccessDialogViewModel'

export interface BookkeepingGrantAccessDialogContainerProps {
  vm: BookkeepingGrantAccessDialogViewModel
}

export const BookkeepingGrantAccessDialogContainer = observer(
  function BookkeepingGrantAccessDialogContainer({
    vm,
  }: BookkeepingGrantAccessDialogContainerProps) {
    const pro = vm.pro
    return (
      <BookkeepingGrantAccessDialog
        showing={vm.modal.showing}
        page={getPage(vm)}
        isClientPerspective={vm.isClientPerspective}
        proDetails={
          vm.hasProDetails && pro
            ? {
                givenName: pro.givenName,
                familyName: pro.familyName,
                email: pro.email,
              }
            : null
        }
        otherSoftwareName={formatAvailableBookkeepingSoftware(vm.otherSoftware)}
        otherSoftwareField={
          <SelectContainer
            label="Bookkeeping software"
            model={vm}
            field="otherSoftware"
            items={availableBookkeepingSoftwareTypes.map(
              (type) =>
                [type, formatAvailableBookkeepingSoftware(type)] as [
                  string,
                  string,
                ],
            )}
            data-test="bookkeeping-grant-access-dialog-other-software-select"
          />
        }
        isConfirmPending={vm.confirmAccessProvided.pending}
        onClose={vm.modal.close}
        onBack={vm.goBack}
        onCopy={vm.copyToClipboard}
        onConfirm={vm.confirmAccessProvided}
      />
    )
  },
)

function getPage(
  vm: BookkeepingGrantAccessDialogViewModel,
): BookkeepingGrantAccessPage {
  if (vm.page.type === 'SelectSoftware') {
    return {
      type: 'SelectSoftware',
      options: allBookkeepingPlatforms.map((platform) => ({
        id: platform,
        label: formatBookkeepingPlatform(platform),
        onSelect: () => vm.selectPlatform(platform),
      })),
    }
  }

  const platform = vm.page.platform
  const loginUrl = getBookkeepingPlatformLoginUrl(platform)
  return {
    type: 'Instructions',
    instructions: match(platform, {
      QuickBooks: 'quickbooks',
      Xero: 'xero',
      Other: 'other',
    }),
    loginLink: loginUrl
      ? { label: formatBookkeepingPlatformLoginName(platform), url: loginUrl }
      : null,
  }
}
